import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"

import AlertBanner from "src/components/AlertBanner"
import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import Chips from "src/components/Chips"
import CollapsibleBar from "src/components/CollapsibleBar"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import SearchableSelect from "src/components/SearchableSelect"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import { getDockwalkActivityLog } from "src/api/DockwalkSummary"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import { useTracker } from "src/hooks/use_tracker"

import { statusBadgeColor } from "src/utils/status_badge_color_helper"
import { titlecase } from "src/utils/string_helpers"

const STATUS_OPTIONS = ["occupied", "vacant", "unchecked"]

const SummaryItems = ({ items }) => {
  const visibleItems = items.filter(Boolean)

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
      {visibleItems.map((item, index) => (
        <React.Fragment key={`${item.label}-${item.value}`}>
          <div className="flex items-center gap-1">
            <span className="font-semibold text-gray-900">{item.value}</span>
            <span>{item.label}</span>
          </div>
          {index < visibleItems.length - 1 && (
            <span className="text-lg text-gray-300">&bull;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

SummaryItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,
}

const WalkActivityLog = ({
  marinaSlug,
  date,
  spaceGroups,
  slipsChecked,
  walksCompleted,
  totalWalks,
  flagsRaised,
}) => {
  const [searchInputValue, setSearchInputValue] = useState("")
  const tracker = useTracker()
  const groupOptions = useMemo(() => spaceGroups, [spaceGroups])
  const table = useDatatable({
    queryKey: ["dockwalkActivityLog", marinaSlug, date],
    fetchFn: ({ spaceGroupIds, ...params }) =>
      getDockwalkActivityLog(marinaSlug, date, {
        ...params,
        space_group_ids: spaceGroupIds,
      }),
    defaultFilters: {
      search: "",
      spaceGroupIds: [],
      statuses: [],
    },
    enabled: Boolean(marinaSlug && date),
    infinite: true,
    syncFiltersToUrl: false,
  })

  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
  })

  useEffect(() => {
    setSearchInputValue(table.filters.search || "")
  }, [table.filters.search])

  const selectedGroupIds = useMemo(
    () => table.filters.spaceGroupIds || [],
    [table.filters.spaceGroupIds]
  )
  const selectedStatuses = useMemo(
    () => table.filters.statuses || [],
    [table.filters.statuses]
  )

  const selectedGroupLabel = useMemo(() => {
    if (selectedGroupIds.length === 0) return "All Groups"
    if (selectedGroupIds.length === 1) {
      return (
        spaceGroups.find((group) => group.id === selectedGroupIds[0])?.name ||
        "All Groups"
      )
    }

    return `${selectedGroupIds.length} ${pluralize(
      "group",
      selectedGroupIds.length
    )} selected`
  }, [selectedGroupIds, spaceGroups])
  const selectedGroupChips = useMemo(
    () =>
      selectedGroupIds.map((groupId) => ({
        id: groupId,
        name:
          spaceGroups.find((group) => group.id === groupId)?.name || groupId,
      })),
    [selectedGroupIds, spaceGroups]
  )
  const selectedStatusChips = useMemo(
    () =>
      selectedStatuses.map((status) => ({
        id: status,
        name: titlecase(status),
      })),
    [selectedStatuses]
  )

  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchInputValue(value)
    debouncedSetSearch(value.trim())
  }

  const clearAllFilters = () => {
    setSearchInputValue("")
    table.clearAllFilters()
  }

  const chips = [
    ...(table.filters.search
      ? [
          {
            key: "search",
            text: table.filters.search,
            onClick: () => {
              setSearchInputValue("")
              table.clearFilter("search")
            },
          },
        ]
      : []),
    ...selectedGroupChips.map((group) => ({
      key: `group-${group.id}`,
      text: group.name,
      onClick: () =>
        table.setFilter(
          "spaceGroupIds",
          selectedGroupIds.filter((id) => id !== group.id)
        ),
    })),
    ...selectedStatusChips.map((status) => ({
      key: `status-${status.id}`,
      text: status.name,
      onClick: () =>
        table.setFilter(
          "statuses",
          selectedStatuses.filter((value) => value !== status.id)
        ),
    })),
  ]

  const columns = useMemo(
    () => [
      {
        key: "spaceName",
        header: "Space",
        sortable: true,
        cardRole: "title",
        cardOrder: 0,
      },
      {
        key: "spaceGroupName",
        header: "Group",
        sortable: true,
        cardRole: "detail",
        cardOrder: 0,
      },
      {
        key: "vesselName",
        header: "Boat",
        sortable: true,
        cardRole: "subtitle",
        cardOrder: 0,
        render: (row) =>
          row.boat ? (
            <BoatPreviewLink boat={row.boat} />
          ) : (
            row.vesselName || "No boat"
          ),
        cardRender: (row) =>
          row.boat ? (
            <BoatPreviewLink boat={row.boat} />
          ) : (
            row.vesselName || "No boat"
          ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        cardRole: "badge",
        cardOrder: 0,
        render: (row) => {
          const display = titlecase(row.status)
          return <Badge color={statusBadgeColor(display)} text={display} />
        },
        cardRender: (row) => {
          const display = titlecase(row.status)
          return (
            <Badge
              color={statusBadgeColor(display)}
              text={display}
              variant="small"
            />
          )
        },
      },
      {
        key: "checkedByName",
        header: "Checked By",
        width: "240px",
        sortable: true,
        cardRole: "detail",
        cardOrder: 1,
        render: (row) => {
          const text = row.checkedByEmail || row.checkedByName || null
          if (!text) return null
          return (
            <span className="block truncate" title={text}>
              {text}
            </span>
          )
        },
      },
      {
        key: "checkedAt",
        header: "Time",
        sortable: true,
        cardRole: "detail",
        cardOrder: 2,
        render: (row) => row.checkedAt || "Not checked",
      },
      {
        key: "flagsCount",
        header: "Flags",
        sortable: true,
        cardRole: "detail",
        cardOrder: 3,
        render: (row) => (
          <div className="flex items-center gap-2">
            <i className="fa-kit fa-m-flag text-red-400" />
            <span>{row.flagsCount}</span>
          </div>
        ),
        cardRender: (row) => (
          <div className="flex items-center gap-2">
            <i className="fa-kit fa-m-flag text-red-400" />
            <span>{pluralize("flag", row.flagsCount, true)}</span>
          </div>
        ),
      },
      {
        key: "notes",
        header: "Notes",
        sortable: true,
        cardRole: "footer",
        cardOrder: 0,
        render: (row) => row.notes || "No notes",
        cardRender: (row) =>
          row.notes ? (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Notes
              </div>
              <div className="mt-1 text-sm text-gray-900">{row.notes}</div>
            </div>
          ) : null,
      },
    ],
    []
  )

  const handleScroll = (event) => {
    if (!table.hasNextPage || table.isFetchingNextPage) return

    const { scrollHeight, scrollTop, clientHeight } = event.target
    if (scrollHeight - scrollTop - 200 <= clientHeight) {
      table.fetchNextPage()
    }
  }

  return (
    <CollapsibleBar
      title="Space Status"
      summary={
        <SummaryItems
          items={[
            {
              value: `${slipsChecked?.checked || 0}/${
                slipsChecked?.total || 0
              }`,
              label: `${pluralize("space", slipsChecked?.total || 0)} checked`,
            },
            {
              value: `${walksCompleted}/${totalWalks}`,
              label: `${pluralize("walk", totalWalks)} complete`,
            },
            { value: flagsRaised, label: pluralize("flag", flagsRaised) },
          ]}
        />
      }
      defaultOpen
      className="shadow-sm"
      ariaLabel="Toggle space status section"
    >
      <div className="min-w-0 space-y-4">
        <div className="flex flex-col gap-2 lg:flex-row">
          <div className="w-full lg:max-w-xl lg:flex-1">
            <Form.IconTextField
              icon={
                <i className="fa-kit fa-search-mdi text-xl text-gray-400" />
              }
              id="dockwalk-activity-log-search"
              isClearable
              onChange={handleSearchChange}
              onClearSelection={() => {
                setSearchInputValue("")
                table.clearFilter("search")
              }}
              placeholder="Search by space, boat, checked by, or notes"
              position="left"
              value={searchInputValue}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              multiple
              value={selectedStatuses}
              onValueChange={(values) => {
                tracker.trackEvent(
                  "dockwalk_summary:activity_log_status_filtered",
                  { statuses: values }
                )
                table.setFilter("statuses", values)
              }}
            >
              <SelectTrigger
                variant="button"
                className="w-full sm:w-auto"
                aria-label="Status"
              >
                Status
              </SelectTrigger>
              <SelectContent className="max-h-none">
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {titlecase(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="w-full sm:w-[260px]">
              <SearchableSelect
                multiple
                value={selectedGroupIds}
                onValueChange={(values) => {
                  tracker.trackEvent(
                    "dockwalk_summary:activity_log_group_filtered",
                    { group_count: values.length }
                  )
                  table.setFilter("spaceGroupIds", values)
                }}
              >
                <SearchableSelect.Trigger aria-label="Space Groups">
                  {selectedGroupLabel}
                </SearchableSelect.Trigger>
                <SearchableSelect.FilteredList
                  options={groupOptions}
                  placeholder="Search groups"
                />
              </SearchableSelect>
            </div>
          </div>
        </div>

        {chips.length > 0 && (
          <Chips onReset={clearAllFilters}>
            {chips.map((chip) => (
              <Chips.Chip
                key={chip.key}
                text={chip.text}
                onClick={chip.onClick}
              />
            ))}
          </Chips>
        )}

        {table.isError ? (
          <AlertBanner type="error" title="No space status to display">
            Try adjusting space group filters or reloading the page if the issue
            persists.
          </AlertBanner>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <DataTableV2
              name="dockwalkActivityLog"
              columns={columns}
              data={table.records}
              hideActions
              hideSettings
              maxHeight="500px"
              rowCount={8}
              onScroll={handleScroll}
              emptyState={
                <div className="flex flex-col text-center">
                  <span className="mb-2 text-xl font-bold">
                    No space status found
                  </span>
                  <span className="text-gray-600">
                    Try changing the selected groups or picking a different
                    date.
                  </span>
                </div>
              }
              {...table.tableProps}
            />
          </div>
        )}
      </div>
    </CollapsibleBar>
  )
}

WalkActivityLog.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  slipsChecked: PropTypes.shape({
    checked: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  walksCompleted: PropTypes.number.isRequired,
  totalWalks: PropTypes.number.isRequired,
  flagsRaised: PropTypes.number.isRequired,
  spaceGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
}

export default WalkActivityLog
